/* eslint-disable complexity */
import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import RadioButton from 'carbon-react/lib/components/radio-button';
import DateInput, { DateChangeEvent } from 'carbon-react/lib/components/date';

import { UnpaidInvoice } from '@futrli-sdk/predict';
import { FormattedMessage } from '@futrli/localisation';
import { Logger } from '@futrli/shared-services';
import { forceLocalDate } from '@futrli/predict/utils/dates';

import * as styles from './bulk-update.scss';
import { UserGridSettingsContext } from '../../../forecast/detail/reports/user-grid-settings/user-grid-settings-context';
import { isFilterByColumn } from '../../../utils/table-filters/filter-table-helper';
import { TABLE_COLUMN } from '../../../utils/table-filters/filter-table-types';
import { invoicesAndBillsColumns } from '../../../forecast/detail/predictions/invoices/invoices';
import { updateUnpaidInvoiceBulk } from '../../../core/api/invoices';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { AssistantSidePanelFormContainer } from '../assistant-side-panle-form-container';

interface Props {
  onClose: () => void;
}

enum BulkUpdateType {
  CUSTOM = 'custom',
  FILTERS = 'filters',
  CUSTOM_EXCLUDE_ALL = 'custom-exclude-all',
  CUSTOM_INCLUDE_ALL = 'custom-include-all',
  CUSTOM_CHANGE_DATE = 'custom-change-date',
  FILTERS_EXCLUDE_ALL = 'filters-exclude-all',
  FILTERS_INCLUDE_ALL = 'filters-include-all',
  FILTERS_CHANGE_DATE = 'filters-change-date'
}

export const BulkUpdateInvoicesBills = ({ onClose }: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [bulkType, setBulkType] = useState(BulkUpdateType.CUSTOM);
  const [bulkSubType, setBulkSubType] = useState(BulkUpdateType.CUSTOM_EXCLUDE_ALL);
  const [paymentDate, setPaymentDate] = useState(new Date());

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';

  const { forecast } = usePredictionsInfoContext();

  const {
    bulkUpdatesInvoicesBills: {
      handleChangeIsCustomSelect,
      handleChangeShowCheckboxes,
      selectedInvoicesBills,
      handleChangeSelectedInvoicesBills,
      handleTriggerInvoicesBillsRefetch,
      filters
    }
  } = useContext(UserGridSettingsContext);

  useEffect(() => {
    handleChangeShowCheckboxes(true);
  }, [handleChangeShowCheckboxes]);

  const handleClose = () => {
    handleChangeShowCheckboxes(false);
    handleChangeSelectedInvoicesBills([]);
    handleChangeIsCustomSelect(true);
    onClose();
  };

  const onRadioClick = (type: BulkUpdateType) => {
    setBulkType(type);
    if (type === BulkUpdateType.CUSTOM) {
      handleChangeIsCustomSelect(true);
      handleChangeSelectedInvoicesBills([]);
      setBulkSubType(BulkUpdateType.CUSTOM_EXCLUDE_ALL);
    } else {
      handleChangeIsCustomSelect(false);
      setBulkSubType(BulkUpdateType.FILTERS_EXCLUDE_ALL);
    }
  };

  const onRadioSubTypeClick = (type: BulkUpdateType) => {
    setBulkSubType(type);
  };

  const handlePaymentDateChange = (e: DateChangeEvent) => {
    setPaymentDate(forceLocalDate(e.target.value.rawValue || ''));
  };

  const handleRecalculate = async () => {
    if (selectedInvoicesBills.length && forecast) {
      const dataPayload: Partial<UnpaidInvoice>[] = [];
      if (bulkSubType === BulkUpdateType.CUSTOM_CHANGE_DATE || bulkSubType === BulkUpdateType.FILTERS_CHANGE_DATE) {
        selectedInvoicesBills.forEach(invoiceBillId => {
          dataPayload.push({
            id: invoiceBillId,
            payment: {
              kind: 'split-by-percent',
              items: [{ date: format(paymentDate, 'yyyy-MM-dd'), percent: 1, amount: 0 }],
              id: invoiceBillId
            }
          });
        });
      } else {
        selectedInvoicesBills.forEach(invoiceBillId => {
          dataPayload.push({
            id: invoiceBillId,
            excluded_from_report:
              bulkSubType === BulkUpdateType.CUSTOM_EXCLUDE_ALL || bulkSubType === BulkUpdateType.FILTERS_EXCLUDE_ALL
                ? true
                : false
          });
        });
      }

      try {
        setIsLoading(true);
        await updateUnpaidInvoiceBulk(dataPayload, workspaceId, organisationId, forecast.id);
        trackEvent(TRACKING_EVENTS.BULK_ACTIONED_INVOICES);
        handleTriggerInvoicesBillsRefetch();
        handleClose();
      } catch (e) {
        Logger.error(e as string);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const filterByNames: ReactNode[] = [];

  if (filters) {
    Object.keys(filters).forEach(k => {
      if (isFilterByColumn(filters, k as TABLE_COLUMN)) {
        const columnName = invoicesAndBillsColumns.find(c => c.name === k);
        if (columnName) {
          filterByNames.push(columnName.display_name);
        }
      }
    });
  }

  return (
    <AssistantSidePanelFormContainer
      onClose={handleClose}
      onSubmit={handleRecalculate}
      closeLabel={<FormattedMessage id="common.close" defaultMessage="Close" />}
      submitLabel={<FormattedMessage id="common.recalculate" defaultMessage="Recalculate" />}
      submitDisabled={selectedInvoicesBills.length === 0 || isLoading}
    >
      <div>
        <div className={styles.radioContainer}>
          <RadioButton
            id="custom-select-invoices-and-bills"
            checked={bulkType === BulkUpdateType.CUSTOM}
            value={BulkUpdateType.CUSTOM}
            onClick={() => onRadioClick(BulkUpdateType.CUSTOM)}
            size="large"
            label={
              <FormattedMessage
                id="predict.custom_select_invoices_bills_bulk_update"
                defaultMessage="Custom select invoices or bills to bulk update"
              />
            }
          />
          {bulkType === BulkUpdateType.CUSTOM && (
            <div style={{ marginLeft: 24 }}>
              {filterByNames.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', marginLeft: 9 }}>
                  <div style={{ marginRight: 8, fontWeight: 500 }}>
                    <FormattedMessage
                      id="predict.filter_applied"
                      defaultMessage="{filterNumber, plural, one {# Filters} other {# Filters}} applied"
                      values={{ filterNumber: filterByNames.length }}
                    />
                    {':'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {filterByNames.map((n, i) => (
                      <span key={i} className={styles.filterByLabel}>
                        {n}
                        {i === filterByNames.length - 1 ? '' : ','}&nbsp;
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="exclude-all-invoices-bills"
                  checked={bulkSubType === BulkUpdateType.CUSTOM_EXCLUDE_ALL}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.CUSTOM_EXCLUDE_ALL)}
                  value={BulkUpdateType.CUSTOM_EXCLUDE_ALL}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_exclude_all_selected_invoices"
                      defaultMessage="Exclude all selected invoices/bills from cash flow"
                    />
                  }
                />
              </div>

              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="include-all-invoices-bills"
                  checked={bulkSubType === BulkUpdateType.CUSTOM_INCLUDE_ALL}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.CUSTOM_INCLUDE_ALL)}
                  value={BulkUpdateType.CUSTOM_INCLUDE_ALL}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_include_all_selected_invoices"
                      defaultMessage="Include all selected invoices/bills in cash flow"
                    />
                  }
                />
              </div>

              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="change-payment-date-all-selected-invoices"
                  checked={bulkSubType === BulkUpdateType.CUSTOM_CHANGE_DATE}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.CUSTOM_CHANGE_DATE)}
                  value={BulkUpdateType.CUSTOM_CHANGE_DATE}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_change_payment_date_of_selected_invoices"
                      defaultMessage="Change payment date of all selected invoices/bills"
                    />
                  }
                />
                {bulkSubType === BulkUpdateType.CUSTOM_CHANGE_DATE && (
                  <DateInput autoFocus value={format(paymentDate, 'dd/MM/yyyy')} onChange={handlePaymentDateChange} />
                )}
              </div>
            </div>
          )}
        </div>
        <div className={styles.radioContainer}>
          <div
            style={{
              marginTop: bulkType === BulkUpdateType.FILTERS ? '12px' : '',
              marginBottom: bulkType === BulkUpdateType.FILTERS ? '12px' : ''
            }}
          >
            <RadioButton
              id="use-filters-for-bulk-update"
              checked={bulkType === BulkUpdateType.FILTERS}
              onClick={() => onRadioClick(BulkUpdateType.FILTERS)}
              value={BulkUpdateType.FILTERS}
              size="large"
              label={
                <FormattedMessage
                  id="predict.use_filters_for_bulk_updates"
                  defaultMessage="Use filters for bulk updates"
                />
              }
            />
          </div>

          {bulkType === BulkUpdateType.FILTERS && (
            <div style={{ marginLeft: 24 }}>
              {filterByNames.length ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', marginLeft: 9 }}>
                  <div style={{ marginRight: 8, fontWeight: 500 }}>
                    <FormattedMessage
                      id="predict.filter_applied"
                      defaultMessage="{filterNumber, plural, one {# Filters} other {# Filters}} applied"
                      values={{ filterNumber: filterByNames.length }}
                    />
                    {':'}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {filterByNames.map((n, i) => (
                      <span key={i} className={styles.filterByLabel}>
                        {n}
                        {i === filterByNames.length - 1 ? '' : ','}&nbsp;
                      </span>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="exclude-all-filtered-invoices-bills"
                  checked={bulkSubType === BulkUpdateType.FILTERS_EXCLUDE_ALL}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.FILTERS_EXCLUDE_ALL)}
                  value={BulkUpdateType.FILTERS_EXCLUDE_ALL}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_exclude_all_filtered_invoices"
                      defaultMessage="Exclude all filtered invoices/bills from cash flow"
                    />
                  }
                />
              </div>

              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="include-all-filtered-invoices-bills"
                  checked={bulkSubType === BulkUpdateType.FILTERS_INCLUDE_ALL}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.FILTERS_INCLUDE_ALL)}
                  value={BulkUpdateType.FILTERS_INCLUDE_ALL}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_include_all_filtered_invoices"
                      defaultMessage="Include all filtered invoices/bills in cash flow"
                    />
                  }
                />
              </div>

              <div className={styles.radioContainerCarbon}>
                <RadioButton
                  id="change-payment-date-filtered-invoices-bills"
                  checked={bulkSubType === BulkUpdateType.FILTERS_CHANGE_DATE}
                  onClick={() => onRadioSubTypeClick(BulkUpdateType.FILTERS_CHANGE_DATE)}
                  value={BulkUpdateType.FILTERS_CHANGE_DATE}
                  size="large"
                  label={
                    <FormattedMessage
                      id="predict.custom_change_payment_date_of_filtered_invoices"
                      defaultMessage="Change payment date of all filtered invoices/bills"
                    />
                  }
                />
                {bulkSubType === BulkUpdateType.FILTERS_CHANGE_DATE && (
                  <DateInput autoFocus value={format(paymentDate, 'dd/MM/yyyy')} onChange={handlePaymentDateChange} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AssistantSidePanelFormContainer>
  );
};
